/**
 * CaMilestoneBackfillPanel — one-click admin utility to convert every open, legacy thread-linked
 * RFI/Submittal to the RFI Response / Submittal Review milestone model
 * (api/admin/backfill-ca-milestones.js, docs/PASSENGERS.md's 2026-09-16 entry). Loops the batched
 * endpoint per table and shows running progress. Idempotent — re-running skips rows that already
 * have a milestone.
 *
 * TEMPORARY one-off utility — remove (with its endpoint) once the legacy backlog is clear.
 */
import { useState, useRef } from 'react'
import { confirmDialog } from '../Dialog.jsx'

async function token() { return window.__clerkGetToken ? await window.__clerkGetToken() : null }

async function call(method, body) {
  const t = await token()
  const res = await fetch('/api/admin/backfill-ca-milestones', {
    method,
    headers: method === 'GET' ? { Authorization: 'Bearer ' + t } : { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
    body: method === 'GET' ? undefined : JSON.stringify(body || {}),
  })
  const d = await res.json()
  if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status))
  return d
}

export default function CaMilestoneBackfillPanel({ dark }) {
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState(null)     // { rfisRemaining, submittalsRemaining }
  const [progress, setProgress] = useState(null) // { rfis: {...}, submittals: {...} }
  const [failures, setFailures] = useState([])
  const [err, setErr] = useState(null)
  const failuresRef = useRef([])

  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.55)'
  const border   = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'

  async function check() {
    setBusy(true); setErr(null)
    try { setStatus(await call('GET')) }
    catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  async function runTable(table, totals) {
    let guard = 0, done = false
    while (!done && guard++ < 200) {
      const d = await call('POST', { table, limit: 3 })
      totals.migrated += d.migrated; totals.failed += d.failed
      for (const r of d.results) if (!r.ok) failuresRef.current.push({ table, ...r })
      setProgress(p => ({ ...p, [table]: { ...totals, remaining: d.remaining } }))
      done = d.done
    }
  }

  async function run() {
    if (!(await confirmDialog({
      title: 'Backfill legacy RFI/Submittal threads?',
      message: 'Every currently open RFI/Submittal still linked to a Cortex Thread gets its own RFI Response / Submittal Review milestone. The old thread is kept, paired to the new milestone as a passenger — nothing is deleted. Safe to re-run; already-migrated rows are skipped.',
      confirmLabel: 'Run backfill',
    }))) return
    setBusy(true); setErr(null); setFailures([]); failuresRef.current = []
    setProgress({ rfis: { migrated: 0, failed: 0 }, submittals: { migrated: 0, failed: 0 } })
    try {
      await runTable('rfis', { migrated: 0, failed: 0 })
      await runTable('submittals', { migrated: 0, failed: 0 })
      setFailures(failuresRef.current)
      setStatus(await call('GET'))
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const btn = (primary) => ({
    padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
    borderRadius: 6, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
    background: primary ? '#1f9d6b' : 'none', color: primary ? '#fff' : textPrim,
    border: primary ? 'none' : `0.5px solid ${border}`,
  })

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: textMut, marginBottom: 14, maxWidth: 640 }}>
        New RFIs/Submittals get their own milestone automatically now (RFI Response / Submittal
        Review) instead of a Cortex Thread. This converts every currently OPEN item still on the
        old thread-only model — closed/withdrawn items are left alone. One-off maintenance utility.
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button style={btn(false)} disabled={busy} onClick={check}>{busy ? 'Working…' : 'Check status'}</button>
        <button style={btn(true)} disabled={busy} onClick={run}>{busy ? 'Working…' : 'Run backfill'}</button>
      </div>
      {err && <div style={{ marginTop: 12, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b' }}>{err}</div>}
      {status && !progress && (
        <div style={{ marginTop: 12, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim }}>
          {status.rfisRemaining} RFI(s) · {status.submittalsRemaining} submittal(s) still on the old thread model
        </div>
      )}
      {progress && (
        <div style={{ marginTop: 12, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim }}>
          <div>RFIs — {busy ? 'working… ' : ''}migrated {progress.rfis.migrated}{progress.rfis.failed ? ` · failed ${progress.rfis.failed}` : ''}</div>
          <div>Submittals — {busy ? 'working… ' : ''}migrated {progress.submittals.migrated}{progress.submittals.failed ? ` · failed ${progress.submittals.failed}` : ''}</div>
          {!busy && <div style={{ marginTop: 4 }}>✓ Done.</div>}
        </div>
      )}
      {failures.length > 0 && (
        <div style={{ marginTop: 10, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#c0392b' }}>
          {failures.map((f, i) => (
            <div key={i}>{f.table} · {f.label} — {f.error}</div>
          ))}
        </div>
      )}
    </div>
  )
}
